import React, { type ReactNode } from 'react';
import { TechnicalSpecsHeroPro } from './TechnicalSpecsHeroPro';

const RING_PRO_TECH_SPECS_BASE = 'web_v2/ring-pro/buy-v2/tech-specs';

/**
 * Data-driven config for the Technical Specifications section.
 * Pass this object to <TechnicalSpecs config={...} />.
 * Omit config to show Ring AIR by default.
 */

export interface TechnicalSpecsImageInfo {
  src: string;
  alt: string;
  textBold: string;
  textNormal: string;
}

/** One row in an accordion section: a translation key and optional bold styling */
export interface TechnicalSpecsContentRow {
  key: string;
  bold?: boolean;
}

/** Data-driven accordion section: title key + content blocks (each block is a list of rows, rendered in a div.content) */
export interface TechnicalSpecsAccordionSection {
  titleKey: string;
  content: TechnicalSpecsContentRow[][];
}

/** Hero: either image list (with built-in animation) or custom React content (e.g. custom animations) */
export type TechnicalSpecsHeroSection =
  | { type: 'images'; images: TechnicalSpecsImageInfo[] }
  | { type: 'custom'; content: ReactNode };

export interface TechnicalSpecsConfig {
  title?: ReactNode;
  heroSection: TechnicalSpecsHeroSection;
  accordionItems: TechnicalSpecsAccordionSection[];
  imageBackgroundColor?: string;
}

type TFunction = (key: string) => string;

/** Ring AIR (default) – used when no config is passed */
export const defaultTechnicalSpecsConfig: TechnicalSpecsConfig = {
  heroSection: {
    type: 'images',
    images: [
      {
        src: 'web_v2/illustrations/air-front-tilt.png/w_1920',
        alt: 'UH Ring AIR',
        textBold: 'ring.techSpec.twoImgInfo.lightA',
        textNormal: 'ring.techSpec.twoImgInfo.lightB',
      },
      {
        src: 'web_v2/illustrations/air-full-tilt.png/w_1920',
        alt: 'UH Ring AIR',
        textBold: 'ring.techSpec.twoImgInfo.sleekA',
        textNormal: 'ring.techSpec.twoImgInfo.sleekB',
      },
    ],
  },
  accordionItems: [
    { titleKey: 'ring.MODEL', content: [[{ key: 'ring.techSpec.model.desc' }]] },
    {
      titleKey: 'ring.BODY',
      content: [
        [
          { key: 'ring.DIMENSIONS', bold: true },
          { key: 'ring.techSpec.body.width' },
          { key: 'ring.techSpec.body.thickness' },
        ],
        [
          { key: 'ring.WEIGHT', bold: true },
          { key: 'ring.techSpec.body.weight' },
        ],
      ],
    },
    {
      titleKey: 'ring.MATERIAL',
      content: [[
        { key: 'ring.techSpec.material.descA' },
        { key: 'ring.techSpec.material.descB' },
      ]],
    },
    {
      titleKey: 'ring.CONNECTIVITY',
      content: [[
        { key: 'ring.techSpec.connectivity.descA' },
        { key: 'ring.techSpec.connectivity.descB' },
        { key: 'ring.techSpec.connectivity.descC' },
        { key: 'ring.techSpec.connectivity.descD' },
        { key: 'ring.techSpec.connectivity.descE' },
      ]],
    },
    {
      titleKey: 'ring.SENSORS',
      content: [[
        { key: 'ring.techSpec.sensors.descA' },
        { key: 'ring.techSpec.sensors.descB' },
        { key: 'ring.techSpec.sensors.descC' },
        { key: 'ring.techSpec.sensors.descD' },
        { key: 'ring.techSpec.sensors.descE' },
        { key: 'ring.techSpec.sensors.descF' },
      ]],
    },
    {
      titleKey: 'ring.BATTERY',
      content: [[
        { key: 'ring.techSpec.battery.descA' },
        { key: 'ring.techSpec.battery.descB' },
        { key: 'ring.techSpec.battery.descC' },
      ]],
    },
  ],
};

/** Options for Ring PRO tech specs hero (optional until assets are ready) */
export interface RingProTechSpecsHeroAssets {
  imageLeftSrc?: string;
  imageLeftAlt?: string;
  imageRightSrc?: string;
  imageRightAlt?: string;
  /** Background image for left panel (rendered as Image) */
  backgroundLeftSrc?: string;
  /** Background image for right panel (rendered as Image) */
  backgroundRightSrc?: string;
  /** Caption under left image (translation keys or plain text) */
  captionLeftTextBold?: string;
  captionLeftTextNormal?: string;
  /** Caption under right image (translation keys or plain text) */
  captionRightTextBold?: string;
  captionRightTextNormal?: string;
}

/** Default Ring PRO tech specs hero assets (ring.png, ring-bg.png, charger-bg.png) */
function getDefaultRingProHeroAssets(): RingProTechSpecsHeroAssets {
  return {
    imageLeftSrc: `${RING_PRO_TECH_SPECS_BASE}/ring.png/w_1920`,
    imageLeftAlt: 'Ultrahuman Ring PRO',
    imageRightSrc: `${RING_PRO_TECH_SPECS_BASE}/case.png/w_1920`,
    imageRightAlt: 'Ring PRO Charger',
    backgroundLeftSrc: `${RING_PRO_TECH_SPECS_BASE}/bg-clean/ring-bg.png/w_1920`,
    backgroundRightSrc: `${RING_PRO_TECH_SPECS_BASE}/bg-clean/charger-bg.png/w_1920`,
    captionLeftTextBold: 'ring.techSpec.ringPro.twoImgInfo.lightA',
    captionLeftTextNormal: 'ring.techSpec.ringPro.twoImgInfo.lightB',
    captionRightTextBold: 'ring.techSpec.ringPro.twoImgInfo.sleekA',
    captionRightTextNormal: 'ring.techSpec.ringPro.twoImgInfo.sleekB',
  };
}

/** Ring PRO – pass to <TechnicalSpecs config={getRingProTechnicalSpecsConfig(t)} /> */
export function getRingProTechnicalSpecsConfig(
  t: TFunction,
  heroAssets?: RingProTechSpecsHeroAssets,
): TechnicalSpecsConfig {
  const assets = heroAssets ?? getDefaultRingProHeroAssets();
  return {
    heroSection: {
      type: 'custom',
      content: React.createElement(TechnicalSpecsHeroPro, {
        imageLeftSrc: assets.imageLeftSrc,
        imageLeftAlt: assets.imageLeftAlt,
        imageRightSrc: assets.imageRightSrc,
        imageRightAlt: assets.imageRightAlt,
        backgroundLeftSrc: assets.backgroundLeftSrc,
        backgroundRightSrc: assets.backgroundRightSrc,
        captionLeftTextBold: assets.captionLeftTextBold,
        captionLeftTextNormal: assets.captionLeftTextNormal,
        captionRightTextBold: assets.captionRightTextBold,
        captionRightTextNormal: assets.captionRightTextNormal,
      }),
    },
    accordionItems: [
      {
        titleKey: 'ring.MATERIAL',
        content: [[
          { key: 'ring.techSpec.ringPro.material.descA' },
          { key: 'ring.techSpec.ringPro.material.descB' },
          { key: 'ring.techSpec.ringPro.material.descC' },
        ]],
      },
      {
        titleKey: 'ring.CONNECTIVITY',
        content: [[
          { key: 'ring.techSpec.ringPro.connectivity.descA' },
          { key: 'ring.techSpec.ringPro.connectivity.descB' },
        ]],
      },
      {
        titleKey: 'ring.techSpec.ringPro.batteryCapacityTitle',
        content: [[{ key: 'ring.techSpec.ringPro.batteryCapacity.descA' }]],
      },
      {
        titleKey: 'ring.techSpec.ringPro.otherKeySpecsTitle',
        content: [[
          { key: 'ring.techSpec.ringPro.otherKeySpecs.descA' },
          { key: 'ring.techSpec.ringPro.otherKeySpecs.descB' },
          { key: 'ring.techSpec.ringPro.otherKeySpecs.descC' },
        ]],
      },
      {
        titleKey: 'ring.techSpec.ringPro.chargingCase.sectionTitle',
        content: [
          [
            { key: 'ring.MATERIAL', bold: true },
            { key: 'ring.techSpec.ringPro.chargingCase.materials.descA' },
          ],
          [
            { key: 'ring.CONNECTIVITY', bold: true },
            { key: 'ring.techSpec.ringPro.chargingCase.connectivity.descA' },
            { key: 'ring.techSpec.ringPro.chargingCase.connectivity.descB' },
          ],
          [
            { key: 'ring.techSpec.ringPro.otherKeySpecsTitle', bold: true },
            { key: 'ring.techSpec.ringPro.chargingCase.otherKeySpecs.descA' },
          ],
          [{ key: 'ring.techSpec.ringPro.chargingCase.otherKeySpecs.descB' }],
          [{ key: 'ring.techSpec.ringPro.chargingCase.otherKeySpecs.descC' }],
          [{ key: 'ring.techSpec.ringPro.chargingCase.otherKeySpecs.descD' }],
          [{ key: 'ring.techSpec.ringPro.chargingCase.otherKeySpecs.descE' }],
          [{ key: 'ring.techSpec.ringPro.chargingCase.otherKeySpecs.descF' }],
          [{ key: 'ring.techSpec.ringPro.chargingCase.otherKeySpecs.descG' }],
        ],
      },
    ],
  };
}

/** Resolve data-driven accordion sections to title + content nodes using t() */
export function resolveAccordionSections(
  sections: TechnicalSpecsAccordionSection[],
  t: TFunction,
): Array<{ title: ReactNode; content: ReactNode }> {
  return sections.map((section) => ({
    title: React.createElement('span', { className: 'bold' }, t(section.titleKey)),
    content: React.createElement(
      React.Fragment,
      null,
      ...section.content.map((block, blockIdx) =>
        React.createElement(
          'div',
          { key: blockIdx, className: 'content' },
          ...block.map((row, i) =>
            React.createElement(
              'div',
              { key: i, className: 'content-row' },
              row.bold
                ? React.createElement('span', { className: 'bold' }, t(row.key))
                : React.createElement('span', null, t(row.key)),
            ),
          ),
        ),
      ),
    ),
  }));
}
